import type { RxDocument } from 'rxdb';

import { useEnabledInstructionsLazy, useEnabledUserSourcesLazy, useInstructionsLazy } from '@/hooks/rxdb-models';

import type { Block, Note } from '@flintmint/core-shared/rxdb';
import type { BlockInstruction, LLMRequestContext } from '../prompt-invocation-builders';

type LLMContextDocuments = {
  noteDocument?: RxDocument<Note>;
  blockDocument?: RxDocument<Block>;
};

type GenerateTextContextParams = {
  note?: RxDocument<Note>;
  block?: RxDocument<Block>;
  activeBlockId?: string;
};

export const useLLMContext = () => {
  const { query: getEnabledInstructions } = useEnabledInstructionsLazy();
  const { query: getEnabledUserSources } = useEnabledUserSourcesLazy();
  const { query: getInstructions } = useInstructionsLazy();

  const getBlocksInstructions = async (note?: RxDocument<Note>) => {
    if (!note) {
      return [];
    }

    const result: BlockInstruction[] = [];

    for (const [blockId, instructionMap] of Object.entries(note.blockInstructions)) {
      const enabledInstructionIds = getEnabledInstructionIds(instructionMap);

      if (enabledInstructionIds.length > 0) {
        const instructions = await getInstructions({ ids: enabledInstructionIds });
        instructions.forEach((instruction) => {
          result.push({ blockId, instruction });
        });
      }
    }

    return result;
  };

  const getEnabledInstructionIds = (instructionMap: Record<string, boolean>): string[] => {
    return Object.entries(instructionMap)
      .filter(([_instructionId, enabled]) => enabled)
      .map(([instructionId, _]) => instructionId);
  };

  const getContext = async ({ noteDocument, blockDocument }: LLMContextDocuments): Promise<LLMRequestContext> => {
    const [documentInstructions, userSources, blocksInstructions] = await Promise.all([
      getEnabledInstructions(noteDocument || blockDocument),
      getEnabledUserSources(noteDocument),
      getBlocksInstructions(noteDocument),
    ]);

    return {
      documentInstructions,
      userSources,
      document: noteDocument || blockDocument!,
      blocksInstructions,
    };
  };

  const getGenerateTextContext = async ({
    note,
    block,
    activeBlockId,
  }: GenerateTextContextParams): Promise<LLMRequestContext> => {
    if (!note && !block) {
      throw new Error('getGenerateTextContext requires either note or block');
    }

    // Generating text within master block
    if (block) {
      const documentInstructions = await getEnabledInstructions(block);
      return {
        document: block,
        documentInstructions,
        userSources: [],
      };
    }

    // Generating text within a note, with active block
    if (note && activeBlockId) {
      const blockInstructionMap = note.blockInstructions[activeBlockId] || {};
      const enabledBlockInstructionIds = getEnabledInstructionIds(blockInstructionMap);

      const [noteInstructions, activeBlockInstructions, userSources] = await Promise.all([
        getEnabledInstructions(note),
        enabledBlockInstructionIds.length > 0 ? getInstructions({ ids: enabledBlockInstructionIds }) : Promise.resolve([]),
        getEnabledUserSources(note),
      ]);

      return {
        document: note,
        documentInstructions: [...noteInstructions, ...activeBlockInstructions], // all relevant instructions merged
        userSources,
      };
    }

    // Generating text within a note, without active block
    const [documentInstructions, userSources] = await Promise.all([getEnabledInstructions(note), getEnabledUserSources(note)]);
    return {
      document: note!,
      documentInstructions,
      userSources,
    };
  };

  return { getContext, getGenerateTextContext };
};
